import React, { useState, useMemo, useCallback } from "react";
import { ListItem, ListItemFormData, WishListProps } from "../../types";
import { useNotifications, ConfirmModal } from "../common";
import { useApiError, useConfirmModal } from "../../hooks";
import { useEvent } from "../../contexts/EventContext";
import ListItemComponent from "./ListItemComponent";
import ListItemForm from "./ListItemForm";
import ListPublicationButton from "./ListPublicationButton";
import PastGiftsBanner from "./PastGiftsBanner";
import { EventSelector } from "../events";
import "./lists.css";
import {
  PlusIcon,
  GiftIcon,
  EnvelopeIcon,
  InfoIcon,
} from "@phosphor-icons/react";

const WishList: React.FC<WishListProps> = ({
  personId,
  eventId,
  items,
  canEdit,
  onItemAdd,
  onItemUpdate,
  onItemDelete,
  listId,
  isLocked,
  onListPublish,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ListItem | null>(null);

  const { showSuccess } = useNotifications();
  const { handleApiError } = useApiError();
  const { isOpen, config, isLoading, showConfirm, handleConfirm, handleCancel } = useConfirmModal();
  const { events, selectedEvent, selectEvent, refreshUserListStatus } =
    useEvent();

  const handleAddSubmit = useCallback(async (
    itemData: ListItemFormData | Partial<ListItemFormData>
  ) => {
    if (onItemAdd && isCompleteListItemFormData(itemData)) {
      try {
        await onItemAdd(itemData);
        setShowAddForm(false);
        // Refresh user list status to update the banner
        refreshUserListStatus();
        showSuccess(
          "Elemento aggiunto!",
          "Il nuovo elemento è stato aggiunto alla lista."
        );
      } catch (err) {
        handleApiError(err);
      }
    }
  }, [onItemAdd, refreshUserListStatus, showSuccess, handleApiError]);

  const handleEditSubmit = async (
    itemData: ListItemFormData | Partial<ListItemFormData>
  ) => {
    if (editingItem && onItemUpdate) {
      try {
        await onItemUpdate(editingItem.id, itemData);
        setEditingItem(null);
        // Refresh user list status to update the banner
        refreshUserListStatus();
        showSuccess("Elemento modificato!", "Le modifiche sono state salvate.");
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  // Type guard to check if data is complete ListItemFormData
  const isCompleteListItemFormData = (
    data: ListItemFormData | Partial<ListItemFormData>
  ): data is ListItemFormData => {
    return typeof data.name === "string" && data.name.trim().length > 0;
  };

  const handleEdit = (item: ListItem) => {
    setEditingItem(item);
  };

  const handleDelete = async (itemId: number) => {
    if (!onItemDelete) return;

    const confirmed = await showConfirm({
      title: "Elimina elemento",
      message: "Sei sicuro di voler eliminare questo elemento dalla tua lista? Questa azione non può essere annullata.",
      confirmText: "Elimina",
      cancelText: "Annulla",
      confirmVariant: "danger"
    });

    if (confirmed) {
      try {
        await onItemDelete(itemId);
        // Refresh user list status to update the banner
        refreshUserListStatus();
        showSuccess(
          "Elemento eliminato!",
          "L'elemento è stato rimosso dalla lista."
        );
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  // Use eventId prop if provided, otherwise use selected event from context
  const currentEventId = eventId || selectedEvent?.id;

  // Memoize initialData to prevent unnecessary re-renders of ListItemForm
  const editingInitialData = useMemo(() => {
    if (!editingItem) return undefined;
    return {
      name: editingItem.name,
      description: editingItem.description,
      link: editingItem.link,
      price: editingItem.price,
    };
  }, [editingItem]);

  return (
    <div className="wish-list">
      {/* Event selector - only show if no eventId prop is provided (for backward compatibility) */}
      {!eventId && events.length > 0 && (
        <div className="event-selector-container">
          <EventSelector
            events={events}
            selectedEventId={selectedEvent?.id}
            onEventSelect={selectEvent}
          />
        </div>
      )}

      {/* Show message if no event is selected */}
      {!currentEventId && events.length > 0 && (
        <div className="no-event-selected">
          <p>Seleziona un evento per gestire la tua lista regalo.</p>
        </div>
      )}

      {/* Show message if no events exist */}
      {events.length === 0 && (
        <div className="no-events">
          <p>
            Non ci sono eventi disponibili. Contatta un amministratore per
            creare un evento.
          </p>
        </div>
      )}

      {/* List management - only show if event is selected */}
      {currentEventId && (
        <>
          {/* Surprise event message and publication button */}
          {selectedEvent?.isSurprise && canEdit && (
            <div className="surprise-list-management">
              {!isLocked ? (
                <div className="surprise-draft-message">
                  <div className="message-content">
                    <h3>
                      <GiftIcon
                        size={20}
                        weight="fill"
                        style={{
                          verticalAlign: "middle",
                          marginRight: "5px",
                          marginBottom: "1px",
                        }}
                      />
                      Questo è un evento a sorpresa!
                    </h3>
                    <p>
                      Prepara la tua lista liberamente: gli altri non la
                      vedranno finché non la renderai pubblica. Una volta
                      pubblicata, non potrai più modificare o eliminare i regali
                      già inseriti, ma potrai aggiungerne di nuovi.
                    </p>
                  </div>
                  {listId && items.length > 0 && (
                    <div className="publication-button-container">
                      <ListPublicationButton
                        listId={listId}
                        isLocked={isLocked || false}
                        onPublish={onListPublish}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="surprise-published-message">
                  <div className="message-content">
                    <h3>
                      <EnvelopeIcon
                        size={20}
                        weight="fill"
                        style={{
                          verticalAlign: "middle",
                          marginRight: "5px",
                          marginBottom: "1px",
                        }}
                      />
                      La tua lista è pubblica e bloccata
                    </h3>
                    <p>
                      Non puoi modificare o eliminare i regali già inseriti, ma
                      puoi aggiungerne di nuovi se vuoi. Le prenotazioni
                      rimangono nascoste per mantenere la sorpresa!
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Past gifts banner - show only if list is not locked */}
          {canEdit && personId && currentEventId && !isLocked && (
            <PastGiftsBanner
              personId={personId}
              eventId={currentEventId}
              onGiftsCopied={() => {
                // Trigger a refresh by calling the parent's load function
                refreshUserListStatus();
                // Also trigger list publish callback to reload items
                if (onListPublish) {
                  onListPublish();
                }
              }}
            />
          )}

          {canEdit && (
            <div className="list-actions">
              {items.length > 0 && !showAddForm && !editingItem && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="add-item-button"
                >
                  <PlusIcon
                    size={14}
                    weight="bold"
                    style={{ marginRight: "5px", marginBottom: "1px" }}
                  />
                  Chiedi un regalo
                </button>
              )}
            </div>
          )}

          {showAddForm && (
            <div className="add-form-container">
              <h3>Aggiungi nuovo regalo</h3>
              {selectedEvent?.isSurprise && isLocked && (
                <div className="locked-list-notice">
                  <p>
                    <InfoIcon size={16} weight="fill" />
                    La tua lista è pubblicata. Il nuovo regalo sarà
                    automaticamente bloccato e visibile agli altri membri.
                  </p>
                </div>
              )}
              <ListItemForm
                onSubmit={handleAddSubmit}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          )}

          {editingItem && (
            <div className="edit-form-container">
              <h3>Modifica elemento</h3>
              <ListItemForm
                initialData={editingInitialData}
                onSubmit={handleEditSubmit}
                onCancel={handleCancelEdit}
              />
            </div>
          )}

          {!showAddForm && !editingItem && (
            <div className="items-container">
              {items.length === 0 ? (
                <div className="empty-list">
                  <p>Nessun elemento nella lista.</p>
                  {canEdit && (
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="add-item-button"
                      style={{ marginTop: "1rem" }}
                    >
                      <PlusIcon
                        size={14}
                        weight="bold"
                        style={{ marginRight: "5px", marginBottom: "1px" }}
                      />
                      Aggiungi il primo regalo che desideri
                    </button>
                  )}
                </div>
              ) : (
                <div className="items-list">
                  {items.map((item) => (
                    <ListItemComponent
                      key={item.id}
                      item={item}
                      canEdit={canEdit && !item.isReserved}
                      canReserve={false}
                      isOwnList={true}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isSurpriseEvent={selectedEvent?.isSurprise}
                      isLocked={isLocked}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Confirm Modal */}
      {config && (
        <ConfirmModal
          isOpen={isOpen}
          title={config.title}
          message={config.message}
          confirmText={config.confirmText}
          cancelText={config.cancelText}
          confirmVariant={config.confirmVariant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default WishList;
